import type { AssessmentDisplayMode, SectionSummary } from "~vue/api/generated/reflections";

/** Anything the participant renderers page through: a question tagged with its section. */
export interface SectionedQuestion {
    section_code?: string | null;
    is_closing?: boolean;
}

/**
 * One page of the multi-question renderer. `topSection` is the section whose
 * title heads the page in grouped mode, and null for a chunked (paginated)
 * page, which shows no section chrome.
 */
export interface SectionPage<Q extends SectionedQuestion> {
    topSection: SectionSummary | null;
    questions: Q[];
}

/** Mirrors the `questions_per_page` model default, for payloads that predate the field. */
export const DEFAULT_QUESTIONS_PER_PAGE = 5;

/** True for the display modes that put several questions on one page. */
export function rendersQuestionPages(mode: AssessmentDisplayMode): boolean {
    return mode === "grouped_by_section" || mode === "paginated";
}

export function sectionsByCode(sections: SectionSummary[]): Record<string, SectionSummary> {
    const map: Record<string, SectionSummary> = {};
    for (const section of sections) {
        map[section.code] = section;
    }
    return map;
}

/**
 * Walk up the parent_code chain to the top-level ancestor. For a question in
 * Strategize this returns Leadership Effectiveness; for a question in Job
 * Family it returns Job Family itself.
 */
export function topSectionForCode(byCode: Record<string, SectionSummary>, code: string | null | undefined): SectionSummary | null {
    if (!code) {
        return null;
    }
    let current = byCode[code] ?? null;
    while (current?.parent_code) {
        const parent = byCode[current.parent_code];
        if (!parent) {
            break;
        }
        current = parent;
    }
    return current ?? null;
}

/**
 * The question's own section, but only when it is nested — i.e. when it
 * warrants a subheading below the top-level section's title.
 */
export function childSectionForCode(byCode: Record<string, SectionSummary>, code: string | null | undefined): SectionSummary | null {
    if (!code) {
        return null;
    }
    const direct = byCode[code] ?? null;
    if (!direct?.parent_code) {
        return null;
    }
    return direct;
}

/**
 * One page per top-level section. Questions arrive ordered by section, so
 * consecutive questions sharing a top-level ancestor form a page; questions
 * without a section collapse into their own (header-less) page.
 */
export function buildSectionPages<Q extends SectionedQuestion>(questions: Q[], byCode: Record<string, SectionSummary>): SectionPage<Q>[] {
    const pages: SectionPage<Q>[] = [];
    let currentCode: string | null | undefined;
    let started = false;
    for (const question of questions) {
        const top = topSectionForCode(byCode, question.section_code);
        const code = top?.code ?? null;
        if (!started || code !== currentCode) {
            pages.push({ topSection: top, questions: [question] });
            currentCode = code;
            started = true;
        } else {
            pages[pages.length - 1].questions.push(question);
        }
    }
    return pages;
}

/**
 * Fixed-size pages in the order the questions arrive. The server has already
 * put a paginated template's questions in participant order, so the page
 * boundaries here say nothing about which section a question belongs to.
 *
 * The closing questions (those outside any section) arrive last and unshuffled.
 * They start a fresh page rather than sharing one with the shuffled questions,
 * and are then chunked at the same page size.
 */
export function buildChunkedPages<Q extends SectionedQuestion>(questions: Q[], questionsPerPage: number): SectionPage<Q>[] {
    const size = Math.max(1, Math.floor(questionsPerPage) || 1);
    const firstClosing = questions.findIndex((question) => question.is_closing);
    const runs = firstClosing === -1 ? [questions] : [questions.slice(0, firstClosing), questions.slice(firstClosing)];
    const pages: SectionPage<Q>[] = [];
    for (const run of runs) {
        for (let start = 0; start < run.length; start += size) {
            pages.push({ topSection: null, questions: run.slice(start, start + size) });
        }
    }
    return pages;
}

/** Questions of one page, each tagged with the sub-section heading to show before it. */
export function buildPageItems<Q extends SectionedQuestion>(questions: Q[], byCode: Record<string, SectionSummary>): { question: Q; subheading: string | null }[] {
    const items: { question: Q; subheading: string | null }[] = [];
    let previousChildCode: string | null = null;
    for (const question of questions) {
        const child = childSectionForCode(byCode, question.section_code);
        const childCode = child?.code ?? null;
        items.push({ question, subheading: child && childCode !== previousChildCode ? child.title : null });
        previousChildCode = childCode;
    }
    return items;
}

/**
 * True when the question at ``index`` opens a new top-level section, i.e. the
 * one-per-page renderer should show its section intro card first. Crossing
 * Strategize → Inspire (both under Leadership) does not count; Leadership →
 * Job Family does.
 */
export function opensTopSection<Q extends SectionedQuestion>(questions: Q[], index: number, byCode: Record<string, SectionSummary>): boolean {
    const question = questions[index];
    if (!question?.section_code) {
        return false;
    }
    const topCode = topSectionForCode(byCode, question.section_code)?.code;
    if (!topCode) {
        return false;
    }
    if (index === 0) {
        return true;
    }
    const previousTopCode = topSectionForCode(byCode, questions[index - 1]?.section_code)?.code;
    return previousTopCode !== topCode;
}

/** How many questions share a top-level section — shown on the section intro card. */
export function questionCountForTopSection<Q extends SectionedQuestion>(questions: Q[], topCode: string | null | undefined, byCode: Record<string, SectionSummary>): number {
    if (!topCode) {
        return 0;
    }
    return questions.filter((question) => topSectionForCode(byCode, question.section_code)?.code === topCode).length;
}
